import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { useQueryParam, withDefault, StringParam } from 'use-query-params'
import { X as XIcon, Check as CheckIcon } from 'react-feather'
import { useHistory } from 'react-router'
import axios from 'axios'

import '~/assets/scss/pages/authentication.scss'
import SuccessIcon from '~/assets/img/icons/success.svg'
import WarningIcon from '~/assets/img/icons/warning.svg'
import LockIcon from '~/assets/img/icons/lock.svg'
import EmailIcon from '~/assets/img/icons/email.svg'
import { AuthLayout } from './components/AuthLayout'
import { queries } from '~/constants/mediaQuery'

function ResetPassword() {
  const history = useHistory()
  const [token] = useQueryParam('token', withDefault(StringParam, ''))
  const [email] = useQueryParam('email', withDefault(StringParam, ''))
  const [password, setPassword] = useState('')
  const [passwordShow, setPasswordShow] = useState(false)
  const [confirmPassword, setConfirmPassword] = useState('')
  const [confirmPasswordShow, setConfirmPasswordShow] = useState(false)
  const [invalidToken, setInvalidToken] = useState(false)
  const [passwordReset, setPasswordReset] = useState(false)
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false)
  const intl = useIntl()

  const isPasswordTouched = password.length || confirmPassword.length
  const isPasswordSame = password === confirmPassword
  const isPasswordLength = password.length >= 8
  const isPasswordUppercase = password.match(/[A-Z]/g)
  const isPasswordLowercase = password.match(/[a-z]/g)
  const isPasswordNumber = password.match(/\d/g)
  const isPasswordSpecialChar = password.match(/\W/g)
  const isRequirementsMet =
    isPasswordSame &&
    isPasswordLength &&
    isPasswordUppercase &&
    isPasswordLowercase &&
    isPasswordNumber &&
    isPasswordSpecialChar

  async function handleSubmit(e) {
    e.preventDefault()
    setResetPasswordLoading(true)

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/reset-password`, {
        email,
        password,
        token,
      })
      setPasswordReset(true)
    } catch (err) {
      setInvalidToken(true)
    }
    setResetPasswordLoading(false)
  }

  useEffect(() => {
    if (!token || !email) history.push('/login')
  }, [token, email])

  return (
    <AuthLayout
      title={
        passwordReset && !invalidToken
          ? 'Your password has been successfully updated'
          : invalidToken
          ? 'Expired link'
          : 'Enter a new password for your account'
      }
      description={
        invalidToken ? 'This link has already been used or has expired.' : ''
      }
    >
      <ResetPasswordDiv>
        {passwordReset && (
          <div className="alert-div">
            <div className="alert-box success">
              <div>
                <img src={SuccessIcon} alt="" />
              </div>
              <div>
                <FormattedMessage id="You can log in with your e-mail and updated password." />
              </div>
            </div>
            <a className="back-button" href="/login">
              <FormattedMessage id="Go back to login" />
            </a>
          </div>
        )}

        {invalidToken && (
          <div className="alert-div">
            <div className="alert-box warning">
              <div>
                <img src={WarningIcon} alt="" />
              </div>
              <div>
                <FormattedMessage id="Try resetting your password again" />
              </div>
            </div>
            <a className="back-button" href="/login">
              <FormattedMessage id="Go back to login" />
            </a>
          </div>
        )}

        {!invalidToken && !passwordReset && (
          <>
            <div className="text-input hidden">
              <div className="label-row">
                <label htmlFor="username">
                  <FormattedMessage id="Email" />
                </label>
              </div>
              <div className="input-row">
                <img src={EmailIcon} alt="" />
                <input
                  hidden
                  id="username"
                  name="username"
                  autoComplete="username"
                  value={email}
                  placeholder={intl.formatMessage({ id: 'Enter your e-mail' })}
                />
              </div>
            </div>
            <div className="password-input">
              <div className="label-row">
                <label htmlFor="new-password">
                  <FormattedMessage id="Enter a password" />
                </label>
              </div>
              <div className="input-row">
                <img src={LockIcon} alt="" />
                <input
                  id="new-password"
                  name="new-password"
                  autoComplete="new-password"
                  passwordrules="minlength: 8; maxlength: 20; required: lower; required: upper; required: digit; required: special;"
                  minLength="8"
                  maxLength="20"
                  type={passwordShow ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••••"
                />
                <button
                  onClick={() => setPasswordShow(prevState => !prevState)}
                >
                  <FormattedMessage id={passwordShow ? 'Hide' : 'Show'} />
                </button>
              </div>
            </div>
            <div className="password-input">
              <div className="label-row">
                <label htmlFor="confirm-password">
                  <FormattedMessage id="Confirm your password" />
                </label>
              </div>
              <div className="input-row">
                <img src={LockIcon} alt="" />
                <input
                  id="confirm-password"
                  name="confirm-password"
                  autoComplete="new-password"
                  passwordrules="minlength: 8; maxlength: 20; required: lower; required: upper; required: digit; required: special;"
                  minLength="8"
                  maxLength="20"
                  type={confirmPasswordShow ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="••••••••••"
                />
                <button
                  onClick={() =>
                    setConfirmPasswordShow(prevState => !prevState)
                  }
                >
                  <FormattedMessage
                    id={confirmPasswordShow ? 'Hide' : 'Show'}
                  />
                </button>
              </div>
            </div>
            <div className="password-requirements">
              <div
                className={
                  isPasswordTouched
                    ? isPasswordSame
                      ? 'requirements-check'
                      : 'requirements-fail'
                    : ''
                }
              >
                {isPasswordTouched ? (
                  isPasswordSame ? (
                    <CheckIcon size={16} color="#48BB78" />
                  ) : (
                    <XIcon size={16} color="#E53E3E" />
                  )
                ) : (
                  <XIcon size={16} color="#718096" />
                )}
                <FormattedMessage id="Both passwords are the same" />
              </div>

              <div
                className={
                  isPasswordTouched
                    ? isPasswordLength
                      ? 'requirements-check'
                      : 'requirements-fail'
                    : ''
                }
              >
                {isPasswordTouched ? (
                  isPasswordLength ? (
                    <CheckIcon size={16} color="#48BB78" />
                  ) : (
                    <XIcon size={16} color="#E53E3E" />
                  )
                ) : (
                  <XIcon size={16} color="#718096" />
                )}
                <FormattedMessage id="8 characters minimum" />
              </div>

              <div
                className={
                  isPasswordTouched
                    ? isPasswordUppercase
                      ? 'requirements-check'
                      : 'requirements-fail'
                    : ''
                }
              >
                {isPasswordTouched ? (
                  isPasswordUppercase ? (
                    <CheckIcon size={16} color="#48BB78" />
                  ) : (
                    <XIcon size={16} color="#E53E3E" />
                  )
                ) : (
                  <XIcon size={16} color="#718096" />
                )}
                <FormattedMessage id="Uppercase letter" />
              </div>

              <div
                className={
                  isPasswordTouched
                    ? isPasswordLowercase
                      ? 'requirements-check'
                      : 'requirements-fail'
                    : ''
                }
              >
                {isPasswordTouched ? (
                  isPasswordLowercase ? (
                    <CheckIcon size={16} color="#48BB78" />
                  ) : (
                    <XIcon size={16} color="#E53E3E" />
                  )
                ) : (
                  <XIcon size={16} color="#718096" />
                )}
                <FormattedMessage id="Lowercase letter" />
              </div>

              <div
                className={
                  isPasswordTouched
                    ? isPasswordNumber
                      ? 'requirements-check'
                      : 'requirements-fail'
                    : ''
                }
              >
                {isPasswordTouched ? (
                  isPasswordNumber ? (
                    <CheckIcon size={16} color="#48BB78" />
                  ) : (
                    <XIcon size={16} color="#E53E3E" />
                  )
                ) : (
                  <XIcon size={16} color="#718096" />
                )}
                <FormattedMessage id="Number" />
              </div>

              <div
                className={
                  isPasswordTouched
                    ? isPasswordSpecialChar
                      ? 'requirements-check'
                      : 'requirements-fail'
                    : ''
                }
              >
                {isPasswordTouched ? (
                  isPasswordSpecialChar ? (
                    <CheckIcon size={16} color="#48BB78" />
                  ) : (
                    <XIcon size={16} color="#E53E3E" />
                  )
                ) : (
                  <XIcon size={16} color="#718096" />
                )}
                <FormattedMessage id="Special character" />
              </div>
            </div>
            <div className="button-menu">
              <button
                className="proceed-button"
                data-loading={resetPasswordLoading}
                disabled={
                  !password.trim() ||
                  !confirmPassword.trim() ||
                  !isRequirementsMet ||
                  resetPasswordLoading
                }
                onClick={handleSubmit}
              >
                <FormattedMessage id="Create new password" />
              </button>
              <a className="back-button" href="/login">
                <FormattedMessage id="Go back to login" />
              </a>
            </div>
          </>
        )}
      </ResetPasswordDiv>
    </AuthLayout>
  )
}

const ResetPasswordDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2rem;

  .text-input {
    position: relative;
    display: flex;
    min-width: 30.5625rem;
    flex-direction: column;
    justify-content: center;
    gap: 0.75rem;

    &.hidden {
      display: none;
    }

    @media (${queries.less.mds}) {
      min-width: 100%;
    }

    .label-row {
      label {
        font-weight: 700;
        color: #000;
        font-size: 1rem;
      }
    }

    .input-row {
      width: 100%;
      max-width: 30.5625rem;
      height: 3rem;

      input {
        height: 3rem;
        width: 100%;
        max-width: 30.5625rem;
        border: 1px solid #d8d8d8;
        border-radius: 0.25rem 0.25rem 0.25rem 0.25rem;
        padding: 0.5rem 1rem 0.5rem 3rem;

        &::placeholder {
          color: #cfcfcf;
        }

        @media (${queries.less.mds}) {
          max-width: 100%;
        }
      }

      img {
        position: absolute;
        left: 1rem;
        bottom: 4px;
        transform: translateY(-50%);
      }

      @media (${queries.less.mds}) {
        max-width: 100%;
      }
    }
  }

  .password-input {
    position: relative;
    display: flex;
    min-width: 30.5625rem;
    flex-direction: column;
    justify-content: center;
    gap: 0.75rem;

    @media (${queries.less.mds}) {
      min-width: 100%;
    }

    .label-row {
      width: 100%;
      max-width: 30.5625rem;
      display: flex;
      justify-content: space-between;
      align-items: center;

      label {
        font-weight: 700;
        color: #000;
        font-size: 1rem;
      }

      @media (${queries.less.mds}) {
        max-width: 100%;
      }
    }

    .input-row {
      width: 100%;
      max-width: 30.5625rem;
      height: 3rem;

      input {
        height: 100%;
        width: 75%;
        border: 1px solid #d8d8d8;
        border-radius: 0.25rem 0 0 0.25rem;
        padding: 0.5rem 1rem 0.5rem 3rem;

        &::placeholder {
          color: #cfcfcf;
        }
      }

      button {
        height: 100%;
        width: 25%;
        border: 1px solid #d8d8d8;
        border-left: none;
        border-radius: 0 0.25rem 0.25rem 0;
        background-color: #fafafa;
        color: #6d7278;
        font-weight: 700;
        font-size: 1rem;
        padding: 0.5rem 1rem;
      }

      img {
        position: absolute;
        left: 1rem;
        bottom: -2px;
        transform: translateY(-50%);
      }

      @media (${queries.less.mds}) {
        max-width: 100%;
      }
    }
  }

  .password-requirements {
    width: 100%;
    max-width: 30.5625rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    color: #718096;
    font-size: 0.75rem;
    font-weight: 700;

    @media (${queries.less.mds}) {
      max-width: 100%;
    }

    > div {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      &.requirements-check {
        color: #48bb78;
      }

      &.requirements-fail {
        color: #e53e3e;
      }
    }
  }

  .button-menu {
    width: 100%;
    max-width: 30.5625rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;

    .proceed-button {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0.75rem 1.5rem;
      border: none;
      border-radius: 0.25rem;
      background-color: #7367f0;
      color: #fff;
      font-size: 1.125rem;
      font-weight: 700;

      &:disabled {
        background-color: #b9b3f7;
        cursor: not-allowed;
      }

      &[data-loading='true'] {
        background-color: #b9b3f7;
        cursor: wait;
      }

      @media (${queries.less.mds}) {
        max-width: 100%;
      }
    }

    .back-button {
      color: #7367f0;
      font-size: 1rem;
      font-style: normal;
      font-weight: 700;
      line-height: 1.5rem;
    }
  }

  .alert-div {
    width: 100%;
    max-width: 30.5625rem;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 2rem;

    .alert-box {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0.75rem 1rem;
      border-radius: 0.375rem;
      gap: 0.75rem;
      color: #2d3748 !important;
    }

    .alert-box.success {
      border: 1px solid #9ae6b4;
      background: #f0fff4;
    }

    .alert-box.warning {
      border: 1px solid #fbd38d;
      background: #fffaf0;
    }
  }
`

export default ResetPassword
